import { ChainId } from '@aave/contract-helpers';
import {
  AaveV2Avalanche,
  AaveV2Ethereum,
  AaveV2Fuji,
  AaveV2Polygon,
  AaveV3Arbitrum,
  AaveV3Avalanche,
  AaveV3Base,
  AaveV3BaseSepolia,
  AaveV3BNB,
  AaveV3Celo,
  AaveV3Ethereum,
  AaveV3EthereumEtherFi,
  AaveV3EthereumHorizon,
  AaveV3EthereumLido,
  AaveV3Gnosis,
  AaveV3InkWhitelabel,
  AaveV3Linea,
  AaveV3Mantle,
  AaveV3MegaEth,
  AaveV3Metis,
  AaveV3Monad,
  AaveV3Optimism,
  AaveV3Plasma,
  AaveV3Polygon,
  AaveV3Scroll,
  AaveV3Soneium,
  AaveV3Sonic,
  AaveV3XLayer,
  AaveV3ZkSync,
  GhoAvalanche,
  GhoGnosis,
  GhoMantle,
  GhoPlasma,
} from '@aave-dao/aave-address-book';
import { ReactNode } from 'react';

// Enable for premissioned market
// import { PermissionView } from 'src/components/transactions/FlowCommons/PermissionView';
export type MarketDataType = {
  v3?: boolean;
  marketTitle: string;
  market: CustomMarket;
  // the network the market operates on
  chainId: ChainId;
  enabledFeatures?: {
    liquiditySwap?: boolean;
    staking?: boolean;
    governance?: boolean;
    faucet?: boolean;
    collateralRepay?: boolean;
    incentives?: boolean;
    permissions?: boolean;
    debtSwitch?: boolean;
    withdrawAndSwitch?: boolean;
    switch?: boolean;
    limit?: boolean;
  };
  permitDisabled?: boolean; // intended to be used for testnets
  isFork?: boolean;
  permissionComponent?: ReactNode;
  subgraphUrl?: string;
  logo?: string;
  externalUrl?: string; // URL for external markets like Aptos
  addresses: {
    LENDING_POOL_ADDRESS_PROVIDER: string;
    LENDING_POOL: string;
    WETH_GATEWAY?: string;
    SWAP_COLLATERAL_ADAPTER?: string;
    REPAY_WITH_COLLATERAL_ADAPTER?: string;
    DEBT_SWITCH_ADAPTER?: string;
    WITHDRAW_SWITCH_ADAPTER?: string;
    FAUCET?: string;
    PERMISSION_MANAGER?: string;
    WALLET_BALANCE_PROVIDER: string;
    L2_ENCODER?: string;
    UI_POOL_DATA_PROVIDER: string;
    UI_INCENTIVE_DATA_PROVIDER?: string;
    COLLECTOR?: string;
    V3_MIGRATOR?: string;
    GHO_TOKEN_ADDRESS?: string;
  };
};
export enum CustomMarket {
  // v3 test networks, all v3.0.1
  sward_robinhood_testnet_v3 = 'sward_robinhood_testnet_v3',
  proto_base_sepolia_v3 = 'proto_base_sepolia_v3',
  // v3 mainnets
  proto_mainnet_v3 = 'proto_mainnet_v3',
  proto_optimism_v3 = 'proto_optimism_v3',
  proto_avalanche_v3 = 'proto_avalanche_v3',
  proto_polygon_v3 = 'proto_polygon_v3',
  proto_arbitrum_v3 = 'proto_arbitrum_v3',
  proto_metis_v3 = 'proto_metis_v3',
  proto_base_v3 = 'proto_base_v3',
  proto_gnosis_v3 = 'proto_gnosis_v3',
  proto_bnb_v3 = 'proto_bnb_v3',
  proto_scroll_v3 = 'proto_scroll_v3',
  proto_lido_v3 = 'proto_lido_v3',
  proto_zksync_v3 = 'proto_zksync_v3',
  proto_etherfi_v3 = 'proto_etherfi_v3',
  proto_linea_v3 = 'proto_linea_v3',
  proto_sonic_v3 = 'proto_sonic_v3',
  proto_celo_v3 = 'proto_celo_v3',
  proto_soneium_v3 = 'proto_soneium_v3',
  proto_horizon_v3 = 'proto_horizon_v3',
  proto_aptos_v3 = 'proto_aptos_v3',
  proto_plasma_v3 = 'proto_plasma_v3',
  proto_ink_v3 = 'proto_ink_v3',
  proto_megaeth_v3 = 'proto_megaeth_v3',
  proto_mantle_v3 = 'proto_mantle_v3',
  proto_xlayer_v3 = 'proto_xlayer_v3',
  proto_monad_v3 = 'proto_monad_v3',
  // v2
  proto_mainnet = 'proto_mainnet',
  proto_avalanche = 'proto_avalanche',
  proto_fuji = 'proto_fuji',
  proto_polygon = 'proto_polygon',

  // external
  // permissioned_market = 'permissioned_market',
}

export const marketsData: {
  [key in keyof typeof CustomMarket]: MarketDataType;
} = {
  [CustomMarket.sward_robinhood_testnet_v3]: {
    marketTitle: 'Sward',
    market: CustomMarket.sward_robinhood_testnet_v3,
    chainId: 46630 as ChainId,
    v3: true,
    permitDisabled: true,
    enabledFeatures: {
      faucet: true,
      governance: false,
      staking: false,
      incentives: false,
      liquiditySwap: false,
      collateralRepay: false,
      debtSwitch: false,
      withdrawAndSwitch: false,
      switch: false,
      limit: false,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: '0x7697215A4CDdeb10785c4b33bE773E0F30a4b2c7',
      LENDING_POOL: '0xC0dB03bB336EAf0350ad6EddDe7C1878E28Bc998',
      WETH_GATEWAY: '0x63205a2f8Dc0745fC8a2b980AC25db585406CC2b',
      WALLET_BALANCE_PROVIDER: '0x8150F60e3f7083B71029dbAF73001159c55ECe72',
      UI_POOL_DATA_PROVIDER: '0xb98713a9430A773564ED5EC1FAc87105EeCAC6bb',
      COLLECTOR: '0x6E4FB4deF89467c0C77a430754Bef4E1b98c9464',
      FAUCET: '0xb07A56BF498fda2B460f73873C31CF9D71bEfee0',
    },
  },
  [CustomMarket.proto_mainnet_v3]: {
    marketTitle: 'Core',
    market: CustomMarket.proto_mainnet_v3,
    chainId: ChainId.mainnet,
    v3: true,
    enabledFeatures: {
      governance: true,
      staking: true,
      liquiditySwap: true,
      collateralRepay: true,
      incentives: true,
      withdrawAndSwitch: true,
      debtSwitch: true,
      switch: true,
      limit: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3Ethereum.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3Ethereum.POOL,
      WETH_GATEWAY: AaveV3Ethereum.WETH_GATEWAY,
      REPAY_WITH_COLLATERAL_ADAPTER: AaveV3Ethereum.REPAY_WITH_COLLATERAL_ADAPTER,
      SWAP_COLLATERAL_ADAPTER: AaveV3Ethereum.SWAP_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: AaveV3Ethereum.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x2dad8162a989cd99d673de4425bb2298db1e1aa2', // AaveV3Ethereum.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3Ethereum.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV3Ethereum.COLLECTOR,
      GHO_TOKEN_ADDRESS: AaveV3Ethereum.ASSETS.GHO.UNDERLYING,
      WITHDRAW_SWITCH_ADAPTER: AaveV3Ethereum.WITHDRAW_SWAP_ADAPTER,
      DEBT_SWITCH_ADAPTER: AaveV3Ethereum.DEBT_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_lido_v3]: {
    marketTitle: 'Prime',
    market: CustomMarket.proto_lido_v3,
    chainId: ChainId.mainnet,
    v3: true,
    logo: '/icons/markets/lido.svg',
    enabledFeatures: {
      governance: true,
      staking: true,
      liquiditySwap: true,
      collateralRepay: true,
      incentives: true,
      withdrawAndSwitch: true,
      debtSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3EthereumLido.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3EthereumLido.POOL,
      WETH_GATEWAY: AaveV3EthereumLido.WETH_GATEWAY,
      REPAY_WITH_COLLATERAL_ADAPTER: AaveV3EthereumLido.REPAY_WITH_COLLATERAL_ADAPTER,
      SWAP_COLLATERAL_ADAPTER: AaveV3EthereumLido.SWAP_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: AaveV3EthereumLido.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x2dad8162a989cd99d673de4425bb2298db1e1aa2', //AaveV3EthereumLido.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3EthereumLido.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV3Ethereum.COLLECTOR,
      WITHDRAW_SWITCH_ADAPTER: AaveV3EthereumLido.WITHDRAW_SWAP_ADAPTER,
      DEBT_SWITCH_ADAPTER: AaveV3EthereumLido.DEBT_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_mainnet]: {
    marketTitle: 'Ethereum',
    market: CustomMarket.proto_mainnet,
    chainId: ChainId.mainnet,
    enabledFeatures: {
      governance: true,
      staking: true,
      liquiditySwap: true,
      collateralRepay: false,
      incentives: true,
      debtSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV2Ethereum.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV2Ethereum.POOL,
      WETH_GATEWAY: AaveV2Ethereum.WETH_GATEWAY,
      REPAY_WITH_COLLATERAL_ADAPTER: AaveV2Ethereum.REPAY_WITH_COLLATERAL_ADAPTER,
      SWAP_COLLATERAL_ADAPTER: AaveV2Ethereum.SWAP_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: AaveV2Ethereum.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: AaveV2Ethereum.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV2Ethereum.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV2Ethereum.COLLECTOR,
      V3_MIGRATOR: AaveV2Ethereum.MIGRATION_HELPER,
      DEBT_SWITCH_ADAPTER: AaveV2Ethereum.DEBT_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_polygon]: {
    marketTitle: 'Polygon',
    market: CustomMarket.proto_polygon,
    chainId: ChainId.polygon,
    enabledFeatures: {
      liquiditySwap: true,
      incentives: true,
      collateralRepay: false,
      debtSwitch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV2Polygon.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV2Polygon.POOL,
      WETH_GATEWAY: AaveV2Polygon.WETH_GATEWAY,
      SWAP_COLLATERAL_ADAPTER: AaveV2Polygon.SWAP_COLLATERAL_ADAPTER,
      REPAY_WITH_COLLATERAL_ADAPTER: AaveV2Polygon.REPAY_WITH_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: AaveV2Polygon.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: AaveV2Polygon.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV2Polygon.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV2Polygon.COLLECTOR,
      V3_MIGRATOR: AaveV2Polygon.MIGRATION_HELPER,
      DEBT_SWITCH_ADAPTER: AaveV2Polygon.DEBT_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_avalanche]: {
    marketTitle: 'Avalanche',
    market: CustomMarket.proto_avalanche,
    chainId: ChainId.avalanche,
    enabledFeatures: {
      liquiditySwap: true,
      incentives: true,
      collateralRepay: false,
      debtSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV2Avalanche.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV2Avalanche.POOL,
      WETH_GATEWAY: AaveV2Avalanche.WETH_GATEWAY,
      SWAP_COLLATERAL_ADAPTER: AaveV2Avalanche.SWAP_COLLATERAL_ADAPTER,
      REPAY_WITH_COLLATERAL_ADAPTER: AaveV2Avalanche.REPAY_WITH_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: AaveV2Avalanche.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: AaveV2Avalanche.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV2Avalanche.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV2Avalanche.COLLECTOR,
      V3_MIGRATOR: AaveV2Avalanche.MIGRATION_HELPER,
      DEBT_SWITCH_ADAPTER: AaveV2Avalanche.DEBT_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_base_v3]: {
    marketTitle: 'Base',
    market: CustomMarket.proto_base_v3,
    v3: true,
    chainId: ChainId.base,
    enabledFeatures: {
      incentives: true,
      liquiditySwap: true,
      withdrawAndSwitch: true,
      collateralRepay: true,
      debtSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3Base.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3Base.POOL,
      WETH_GATEWAY: AaveV3Base.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: AaveV3Base.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x0c6bc4a12039788be08f87e87cff87fedbd1d386', // AaveV3Base.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3Base.UI_INCENTIVE_DATA_PROVIDER,
      L2_ENCODER: AaveV3Base.L2_ENCODER,
      COLLECTOR: AaveV3Base.COLLECTOR,
      REPAY_WITH_COLLATERAL_ADAPTER: AaveV3Base.REPAY_WITH_COLLATERAL_ADAPTER,
      SWAP_COLLATERAL_ADAPTER: AaveV3Base.SWAP_COLLATERAL_ADAPTER,
      WITHDRAW_SWITCH_ADAPTER: AaveV3Base.WITHDRAW_SWAP_ADAPTER,
      DEBT_SWITCH_ADAPTER: AaveV3Base.DEBT_SWAP_ADAPTER,
      GHO_TOKEN_ADDRESS: AaveV3Base.ASSETS.GHO.UNDERLYING,
    },
  },

  [CustomMarket.proto_arbitrum_v3]: {
    marketTitle: 'Arbitrum',
    market: CustomMarket.proto_arbitrum_v3,
    v3: true,
    chainId: ChainId.arbitrum_one,
    enabledFeatures: {
      incentives: true,
      liquiditySwap: true,
      collateralRepay: true,
      debtSwitch: true,
      withdrawAndSwitch: true,
      switch: true,
      limit: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3Arbitrum.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3Arbitrum.POOL,
      WETH_GATEWAY: AaveV3Arbitrum.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: AaveV3Arbitrum.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x91e04cf78e53aebe609e8a7f2003e7eecd743f2b', // AaveV3Arbitrum.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3Arbitrum.UI_INCENTIVE_DATA_PROVIDER,
      L2_ENCODER: AaveV3Arbitrum.L2_ENCODER,
      COLLECTOR: AaveV3Arbitrum.COLLECTOR,
      SWAP_COLLATERAL_ADAPTER: AaveV3Arbitrum.SWAP_COLLATERAL_ADAPTER,
      REPAY_WITH_COLLATERAL_ADAPTER: AaveV3Arbitrum.REPAY_WITH_COLLATERAL_ADAPTER,
      DEBT_SWITCH_ADAPTER: AaveV3Arbitrum.DEBT_SWAP_ADAPTER,
      WITHDRAW_SWITCH_ADAPTER: AaveV3Arbitrum.WITHDRAW_SWAP_ADAPTER,
      GHO_TOKEN_ADDRESS: AaveV3Arbitrum.ASSETS.GHO.UNDERLYING,
    },
  },
  [CustomMarket.proto_base_sepolia_v3]: {
    marketTitle: 'Base Sepolia',
    market: CustomMarket.proto_base_sepolia_v3,
    v3: true,
    permitDisabled: true,
    chainId: ChainId.base_sepolia,
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3BaseSepolia.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3BaseSepolia.POOL,
      WETH_GATEWAY: AaveV3BaseSepolia.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: AaveV3BaseSepolia.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x3cB7B00B6C09B71998124196691e8bF2694De863', // AaveV3BaseSepolia.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3BaseSepolia.UI_INCENTIVE_DATA_PROVIDER,
      L2_ENCODER: AaveV3BaseSepolia.L2_ENCODER,
      FAUCET: '0xD9145b5F45Ad4519c7ACcD6E0A4A82e83bB8A6Dc',
    },
  },
  [CustomMarket.proto_avalanche_v3]: {
    marketTitle: 'Avalanche',
    market: CustomMarket.proto_avalanche_v3,
    v3: true,
    chainId: ChainId.avalanche,
    enabledFeatures: {
      liquiditySwap: true,
      incentives: true,
      collateralRepay: true,
      debtSwitch: true,
      withdrawAndSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3Avalanche.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3Avalanche.POOL,
      WETH_GATEWAY: AaveV3Avalanche.WETH_GATEWAY,
      REPAY_WITH_COLLATERAL_ADAPTER: AaveV3Avalanche.REPAY_WITH_COLLATERAL_ADAPTER,
      SWAP_COLLATERAL_ADAPTER: AaveV3Avalanche.SWAP_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: AaveV3Avalanche.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0xFBa4Df643205c5400BC3e05a1E67E0dFaEeeb41F', // AaveV3Avalanche.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3Avalanche.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV3Avalanche.COLLECTOR,
      DEBT_SWITCH_ADAPTER: AaveV3Avalanche.DEBT_SWAP_ADAPTER,
      WITHDRAW_SWITCH_ADAPTER: AaveV3Avalanche.WITHDRAW_SWAP_ADAPTER,
      GHO_TOKEN_ADDRESS: GhoAvalanche.GHO_TOKEN,
    },
  },
  [CustomMarket.proto_linea_v3]: {
    marketTitle: 'Linea',
    market: CustomMarket.proto_linea_v3,
    chainId: ChainId.linea,
    logo: '/icons/markets/linea.svg',
    v3: true,
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3Linea.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3Linea.POOL,
      WETH_GATEWAY: AaveV3Linea.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: AaveV3Linea.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0xc851e6147dce6a469cc33be3121b6b2d4cad2763', // AaveV3Linea.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3Linea.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV3Linea.COLLECTOR,
    },
    enabledFeatures: {
      liquiditySwap: true,
      withdrawAndSwitch: true,
      collateralRepay: true,
      debtSwitch: true,
      switch: true,
    },
  },
  [CustomMarket.proto_sonic_v3]: {
    marketTitle: 'Sonic',
    market: CustomMarket.proto_sonic_v3,
    chainId: ChainId.sonic,
    v3: true,
    enabledFeatures: {
      collateralRepay: true,
      liquiditySwap: true,
      debtSwitch: true,
      withdrawAndSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3Sonic.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3Sonic.POOL,
      WETH_GATEWAY: AaveV3Sonic.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: AaveV3Sonic.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0xe28e2c8d240dd5ebd0adcab86fbd79df7a052034', // AaveV3Sonic.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3Sonic.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV3Sonic.COLLECTOR,
      SWAP_COLLATERAL_ADAPTER: AaveV3Sonic.SWAP_COLLATERAL_ADAPTER,
      REPAY_WITH_COLLATERAL_ADAPTER: AaveV3Sonic.REPAY_WITH_COLLATERAL_ADAPTER,
      DEBT_SWITCH_ADAPTER: AaveV3Sonic.DEBT_SWAP_ADAPTER,
      WITHDRAW_SWITCH_ADAPTER: AaveV3Sonic.WITHDRAW_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_optimism_v3]: {
    marketTitle: 'OP',
    market: CustomMarket.proto_optimism_v3,
    v3: true,
    chainId: ChainId.optimism,
    enabledFeatures: {
      incentives: true,
      collateralRepay: true,
      liquiditySwap: true,
      debtSwitch: true,
      withdrawAndSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3Optimism.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3Optimism.POOL,
      WETH_GATEWAY: AaveV3Optimism.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: AaveV3Optimism.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x68100bd5345ea474d93577127c11f39ff8463e93', // AaveV3Optimism.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3Optimism.UI_INCENTIVE_DATA_PROVIDER,
      L2_ENCODER: AaveV3Optimism.L2_ENCODER,
      COLLECTOR: AaveV3Optimism.COLLECTOR,
      SWAP_COLLATERAL_ADAPTER: AaveV3Optimism.SWAP_COLLATERAL_ADAPTER,
      REPAY_WITH_COLLATERAL_ADAPTER: AaveV3Optimism.REPAY_WITH_COLLATERAL_ADAPTER,
      DEBT_SWITCH_ADAPTER: AaveV3Optimism.DEBT_SWAP_ADAPTER,
      WITHDRAW_SWITCH_ADAPTER: AaveV3Optimism.WITHDRAW_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_horizon_v3]: {
    marketTitle: 'Aave Horizon',
    market: CustomMarket.proto_horizon_v3,
    chainId: ChainId.mainnet,
    v3: true,
    logo: '/icons/markets/horizon.svg',
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: '0x5D39E06b825C1F2B80bf2756a73e28eFAA128ba0',
      LENDING_POOL: '0xAe05Cd22df81871bc7cC2a04BeCfb516bFe332C8',
      WETH_GATEWAY: '0x973195fB8F67F5B0afe7beDB2A02cec829d89991',
      // REPAY_WITH_COLLATERAL_ADAPTER: AaveV3EthereumLido.REPAY_WITH_COLLATERAL_ADAPTER,
      // SWAP_COLLATERAL_ADAPTER: AaveV3EthereumLido.SWAP_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: '0xd8F7829ceB2692C90e418e0963b5Cbcbafc260Bd',
      UI_POOL_DATA_PROVIDER: '0x2dad8162a989cd99d673de4425bb2298db1e1aa2', // AaveV3Ethereum.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: '0xC5E1717BEafC680E0148DD561591410b69650a5F',
      COLLECTOR: AaveV3EthereumHorizon.COLLECTOR,
      // WITHDRAW_SWITCH_ADAPTER: AaveV3EthereumLido.WITHDRAW_SWAP_ADAPTER,
      // DEBT_SWITCH_ADAPTER: AaveV3EthereumLido.DEBT_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_plasma_v3]: {
    marketTitle: 'Plasma',
    market: CustomMarket.proto_plasma_v3,
    chainId: ChainId.plasma,
    v3: true,
    logo: '/icons/networks/plasma.svg',
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3Plasma.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3Plasma.POOL,
      WETH_GATEWAY: AaveV3Plasma.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: AaveV3Plasma.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x2E549104c516b8657A7D888494DfbAbD7C70b464', // AaveV3Plasma.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3Plasma.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV3Plasma.COLLECTOR,
      GHO_TOKEN_ADDRESS: GhoPlasma.GHO_TOKEN,
    },
    enabledFeatures: {
      // liquiditySwap: true,
      // withdrawAndSwitch: true,
      // collateralRepay: true,
      // debtSwitch: true,
      // switch: true,
    },
  },
  [CustomMarket.proto_polygon_v3]: {
    marketTitle: 'Polygon',
    market: CustomMarket.proto_polygon_v3,
    chainId: ChainId.polygon,
    v3: true,
    enabledFeatures: {
      liquiditySwap: true,
      incentives: true,
      collateralRepay: true,
      debtSwitch: true,
      withdrawAndSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3Polygon.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3Polygon.POOL,
      WETH_GATEWAY: AaveV3Polygon.WETH_GATEWAY,
      REPAY_WITH_COLLATERAL_ADAPTER: AaveV3Polygon.REPAY_WITH_COLLATERAL_ADAPTER,
      SWAP_COLLATERAL_ADAPTER: AaveV3Polygon.SWAP_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: AaveV3Polygon.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x66E1aBdb06e7363a618D65a910c540dfED23754f', // AaveV3Polygon.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3Polygon.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV3Polygon.COLLECTOR,
      DEBT_SWITCH_ADAPTER: AaveV3Polygon.DEBT_SWAP_ADAPTER,
      WITHDRAW_SWITCH_ADAPTER: AaveV3Polygon.WITHDRAW_SWAP_ADAPTER,
    },
  },

  [CustomMarket.proto_ink_v3]: {
    marketTitle: 'Ink',
    market: CustomMarket.proto_ink_v3,
    chainId: ChainId.ink,
    v3: true,
    logo: '/icons/networks/ink.svg',
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3InkWhitelabel.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3InkWhitelabel.POOL,
      WETH_GATEWAY: AaveV3InkWhitelabel.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: AaveV3InkWhitelabel.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x2E549104c516b8657A7D888494DfbAbD7C70b464', // AaveV3InkWhitelabel.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3InkWhitelabel.UI_INCENTIVE_DATA_PROVIDER,
      GHO_TOKEN_ADDRESS: AaveV3InkWhitelabel.ASSETS.GHO.UNDERLYING,
      COLLECTOR: AaveV3InkWhitelabel.COLLECTOR,
    },
  },

  [CustomMarket.proto_megaeth_v3]: {
    marketTitle: 'MegaETH',
    market: CustomMarket.proto_megaeth_v3,
    chainId: 4326 as ChainId,
    v3: true,
    logo: '/icons/networks/megaeth.svg',
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3MegaEth.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3MegaEth.POOL,
      WETH_GATEWAY: AaveV3MegaEth.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: AaveV3MegaEth.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0xE28E2c8d240dd5eBd0adcab86fbD79df7a052034', // AaveV3MegaEth.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3MegaEth.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV3MegaEth.COLLECTOR,
      L2_ENCODER: AaveV3MegaEth.L2_ENCODER,
    },
  },
  [CustomMarket.proto_xlayer_v3]: {
    marketTitle: 'X Layer',
    market: CustomMarket.proto_xlayer_v3,
    chainId: ChainId.xlayer,
    logo: '/icons/networks/xlayer.svg',

    v3: true,
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3XLayer.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3XLayer.POOL,
      WETH_GATEWAY: AaveV3XLayer.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: AaveV3XLayer.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0xc851e6147dce6a469cc33be3121b6b2d4cad2763', // AaveV3XLayer.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3XLayer.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV3XLayer.COLLECTOR,
    },
  },
  [CustomMarket.proto_mantle_v3]: {
    marketTitle: 'Mantle',
    market: CustomMarket.proto_mantle_v3,
    chainId: ChainId.mantle,
    v3: true,
    logo: '/icons/networks/mantle.svg',
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3Mantle.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3Mantle.POOL,
      WETH_GATEWAY: AaveV3Mantle.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: AaveV3Mantle.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0xc851e6147dce6a469cc33be3121b6b2d4cad2763', // AaveV3Mantle.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3Mantle.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV3Mantle.COLLECTOR,
      L2_ENCODER: AaveV3Mantle.L2_ENCODER,
      GHO_TOKEN_ADDRESS: GhoMantle.GHO_TOKEN,
    },
  },
  [CustomMarket.proto_fuji]: {
    marketTitle: 'Avalanche Fuji',
    market: CustomMarket.proto_fuji,
    chainId: ChainId.fuji,
    enabledFeatures: {
      faucet: true,
      incentives: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV2Fuji.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV2Fuji.POOL,
      WETH_GATEWAY: AaveV2Fuji.WETH_GATEWAY,
      FAUCET: AaveV2Fuji.FAUCET,
      WALLET_BALANCE_PROVIDER: AaveV2Fuji.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: AaveV2Fuji.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV2Fuji.UI_INCENTIVE_DATA_PROVIDER,
    },
  },

  [CustomMarket.proto_gnosis_v3]: {
    marketTitle: 'Gnosis',
    market: CustomMarket.proto_gnosis_v3,
    chainId: ChainId.xdai,
    v3: true,
    enabledFeatures: {
      liquiditySwap: true,
      collateralRepay: true,
      debtSwitch: true,
      withdrawAndSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3Gnosis.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3Gnosis.POOL,
      WETH_GATEWAY: AaveV3Gnosis.WETH_GATEWAY,
      REPAY_WITH_COLLATERAL_ADAPTER: AaveV3Gnosis.REPAY_WITH_COLLATERAL_ADAPTER,
      SWAP_COLLATERAL_ADAPTER: AaveV3Gnosis.SWAP_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: AaveV3Gnosis.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x0C6BC4a12039788be08F87e87Cff87FEDbd1D386', // AaveV3Gnosis.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3Gnosis.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV3Gnosis.COLLECTOR,
      DEBT_SWITCH_ADAPTER: AaveV3Gnosis.DEBT_SWAP_ADAPTER,
      WITHDRAW_SWITCH_ADAPTER: AaveV3Gnosis.WITHDRAW_SWAP_ADAPTER,
      GHO_TOKEN_ADDRESS: GhoGnosis.GHO_TOKEN,
    },
  },
  [CustomMarket.proto_bnb_v3]: {
    marketTitle: 'BNB Chain',
    market: CustomMarket.proto_bnb_v3,
    chainId: ChainId.bnb,
    v3: true,
    enabledFeatures: {
      liquiditySwap: true,
      collateralRepay: true,
      debtSwitch: true,
      withdrawAndSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3BNB.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3BNB.POOL,
      WETH_GATEWAY: AaveV3BNB.WETH_GATEWAY,
      REPAY_WITH_COLLATERAL_ADAPTER: AaveV3BNB.REPAY_WITH_COLLATERAL_ADAPTER,
      SWAP_COLLATERAL_ADAPTER: AaveV3BNB.SWAP_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: AaveV3BNB.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x68100bD5345eA474D93577127C11F39FF8463e93', // AaveV3BNB.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3BNB.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV3BNB.COLLECTOR,
      DEBT_SWITCH_ADAPTER: AaveV3BNB.DEBT_SWAP_ADAPTER,
      WITHDRAW_SWITCH_ADAPTER: AaveV3BNB.WITHDRAW_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_monad_v3]: {
    marketTitle: 'Monad',
    market: CustomMarket.proto_monad_v3,
    chainId: 143 as ChainId,
    logo: '/icons/networks/monad.svg',
    v3: true,
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3Monad.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3Monad.POOL,
      WETH_GATEWAY: AaveV3Monad.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: AaveV3Monad.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: AaveV3Monad.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3Monad.UI_INCENTIVE_DATA_PROVIDER,
      GHO_TOKEN_ADDRESS: '0xfc421aD3C883Bf9E7C4f42dE845C4e4405799e73',
      COLLECTOR: AaveV3Monad.COLLECTOR,
    },
  },
  [CustomMarket.proto_scroll_v3]: {
    marketTitle: 'Scroll',
    market: CustomMarket.proto_scroll_v3,
    chainId: ChainId.scroll,
    v3: true,
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3Scroll.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3Scroll.POOL,
      WETH_GATEWAY: AaveV3Scroll.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: AaveV3Scroll.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0xe28e2c8d240dd5ebd0adcab86fbd79df7a052034', // AaveV3Scroll.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3Scroll.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV3Scroll.COLLECTOR,
    },
  },
  [CustomMarket.proto_zksync_v3]: {
    marketTitle: 'ZKsync',
    market: CustomMarket.proto_zksync_v3,
    chainId: ChainId.zksync,
    v3: true,
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3ZkSync.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3ZkSync.POOL,
      WETH_GATEWAY: AaveV3ZkSync.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: AaveV3ZkSync.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x756Ff6722543F12d25396Ea646B0F2C96dA70c3e', // AaveV3ZkSync.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3ZkSync.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV3ZkSync.COLLECTOR,
    },
  },
  [CustomMarket.proto_aptos_v3]: {
    marketTitle: 'Aptos',
    market: CustomMarket.proto_aptos_v3,
    v3: true,
    chainId: ChainId.mainnet, // Using mainnet since Aptos is external
    logo: '/icons/markets/aptos.svg',
    externalUrl: 'https://aptos.aave.com',
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: '',
      LENDING_POOL: '',
      WALLET_BALANCE_PROVIDER: '',
      UI_POOL_DATA_PROVIDER: '',
      UI_INCENTIVE_DATA_PROVIDER: '',
    },
  },

  [CustomMarket.proto_celo_v3]: {
    marketTitle: 'Celo',
    market: CustomMarket.proto_celo_v3,
    chainId: ChainId.celo,
    v3: true,
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3Celo.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3Celo.POOL,
      WALLET_BALANCE_PROVIDER: AaveV3Celo.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0xc851e6147dcE6A469CC33BE3121b6B2D4CaD2763', // AaveV3Celo.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3Celo.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV3Celo.COLLECTOR,
    },
  },
  [CustomMarket.proto_soneium_v3]: {
    marketTitle: 'Soneium',
    market: CustomMarket.proto_soneium_v3,
    chainId: ChainId.soneium,
    v3: true,
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3Soneium.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3Soneium.POOL,
      WETH_GATEWAY: AaveV3Soneium.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: AaveV3Soneium.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0xc851e6147dce6a469cc33be3121b6b2d4cad2763', // AaveV3Soneium.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3Soneium.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV3Soneium.COLLECTOR,
    },
  },
  [CustomMarket.proto_etherfi_v3]: {
    marketTitle: 'EtherFi',
    market: CustomMarket.proto_etherfi_v3,
    chainId: ChainId.mainnet,
    v3: true,
    logo: '/icons/markets/etherfi.svg',
    enabledFeatures: {
      governance: true,
      staking: true,
      liquiditySwap: true,
      collateralRepay: true,
      incentives: true,
      withdrawAndSwitch: true,
      debtSwitch: true,
      switch: false,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3EthereumEtherFi.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3EthereumEtherFi.POOL,
      WETH_GATEWAY: AaveV3EthereumEtherFi.WETH_GATEWAY,
      REPAY_WITH_COLLATERAL_ADAPTER: AaveV3EthereumEtherFi.REPAY_WITH_COLLATERAL_ADAPTER,
      SWAP_COLLATERAL_ADAPTER: AaveV3EthereumEtherFi.SWAP_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: AaveV3EthereumEtherFi.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x2dad8162a989cd99d673de4425bb2298db1e1aa2', // AaveV3EthereumEtherFi.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3EthereumEtherFi.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV3EthereumEtherFi.COLLECTOR,
      WITHDRAW_SWITCH_ADAPTER: AaveV3EthereumEtherFi.WITHDRAW_SWAP_ADAPTER,
      DEBT_SWITCH_ADAPTER: AaveV3EthereumEtherFi.DEBT_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_metis_v3]: {
    marketTitle: 'Metis',
    market: CustomMarket.proto_metis_v3,
    chainId: ChainId.metis_andromeda,
    v3: true,
    enabledFeatures: {
      incentives: true,
    },
    subgraphUrl: 'https://metisapi.0xgraph.xyz/subgraphs/name/aave/protocol-v3-metis',
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: AaveV3Metis.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: AaveV3Metis.POOL,
      WETH_GATEWAY: '0x0', // not applicable for Metis
      WALLET_BALANCE_PROVIDER: AaveV3Metis.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x5c5228ac8bc1528482514af3e27e692495148717', // AaveV3Metis.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: AaveV3Metis.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: AaveV3Metis.COLLECTOR,
    },
  },
} as const;

export const findByChainId = (chainId: ChainId) => {
  return Object.values(marketsData).find((market) => market.chainId === chainId);
};
